import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import relativeTime from 'dayjs/plugin/relativeTime';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import 'dayjs/locale/ja';

/**
 * @author anhquoctran
 * @description Common date and time functions. Built on top of Dayjs.
 * @summary Reference docs: {@link https://day.js.org/docs/en/display/display}
 */
export default (function () {
  const DEFAULT_TIMEZONE = 'Asia/Tokyo';

  dayjs.extend(utc);
  dayjs.extend(LocalizedFormat);
  dayjs.extend(timezone);
  dayjs.extend(isBetween);
  dayjs.extend(isSameOrBefore);
  dayjs.extend(isSameOrAfter);
  dayjs.extend(relativeTime);
  dayjs.extend(advancedFormat);

  dayjs.locale('ja');

  dayjs.tz.setDefault(DEFAULT_TIMEZONE);

  function getCurrentTimezone() {
    return dayjs.tz.guess() || DEFAULT_TIMEZONE;
  }

  return Object.freeze({

    /**
     * Get current browser's timezone
     */
    tz: getCurrentTimezone(),

    /**
     * Format a date input to human-readable formatted string
     * @param dateInput Date time input to format
     * @param format Specific DayJS format to format date. Default is 'YYYY.MM.DD'
     * @return
     */
    formatDate(dateInput: string | Date, format: string): string {
      if (dateInput) {
        return dayjs(dateInput)
          .tz(getCurrentTimezone())
          .format(format);
      }
      return '-';
    },

    today: dayjs().tz(getCurrentTimezone()).startOf('date').toDate(),

    /**
     * Current date now
     */
    now: dayjs().tz(getCurrentTimezone()).toDate(),

    /**
     * Current date now as UTC (+00:00) time
     */
    nowUTC: dayjs.utc().toDate(),

    /**
     * Current date now as the UNIX timestamp
     */
    nowUnix: dayjs().unix(),

    /**
     * Current DayJS with preconfigured settings (locales, plugins)
     */
    dayjs
  });
})();
